import { useTranslation } from 'react-i18next';
import {
    type AvailabilityProduct,
    BookingStatus,
    PaxData,
    useCreatePackageBooking,
} from '@travelity/api/services';
import { Stack } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { PaxPicker } from '@travelity/form';
import { FormProvider, useForm } from 'react-hook-form';
import SelectCustomer from '@components/select-customer/select-customer';
import { LoadingOverlay } from '@travelity/ui/loading-overlay';
import { PackageBookingDiscount } from './components/PackageBookingDiscount';
import { PackageBookingSourceSelection } from './components/PackageBookingSourceSelection';
import { PackageBookingDateStartDuration } from './components/PackageBookingDateStartDuration';
import { PackageBookingNotesSelection } from './components/PackageBookingNotesSelection';
import { PackageAvailableTimePicker } from './components/PackageAvailableTimePicker';
import { PackageBookingFooter } from './components/PackageBookingFooter';
import { PackageBookingSideformHead } from './components/PackageBookingSideformHead';
import { PackageCapacity } from './components/PackageCapacity';
import { PackageProductOptions } from './components/PackageProductOptions';
import {
    buildPackageDefaultValues,
    type PackageBookingFormValues,
    toCreatePackageBookingsReqDto,
} from './package-form.helpers';
import { useToast } from '@/hooks';

interface PackageBookingSideformProps {
    selectedEvent: AvailabilityProduct;
    selectedTime: number;
    pax: PaxData;
    discard: () => void;
    onSuccess: () => void;
}

const PackageBookingSideform = ({
    selectedEvent,
    selectedTime,
    pax,
    discard,
    onSuccess,
}: PackageBookingSideformProps) => {
    const { t } = useTranslation('product');
    const { showError, showSuccess } = useToast();
    const { mutate: createPackageBooking, isLoading: isSaving } =
        useCreatePackageBooking();
    const [maxAbsoluteDiscount, setMaxAbsoluteDiscount] = useState<number>();
    const packageDefaultValues = useMemo(
        () => buildPackageDefaultValues(selectedEvent, selectedTime, pax),
        [selectedEvent, selectedTime, pax]
    );

    const methods = useForm<PackageBookingFormValues>({
        mode: 'onChange',
        defaultValues: packageDefaultValues,
    });

    useEffect(() => {
        methods.reset(packageDefaultValues);
    }, [methods, packageDefaultValues]);

    useEffect(() => {
        setMaxAbsoluteDiscount(undefined);
    }, [selectedEvent.id, selectedTime]);

    const onSubmit = (
        values: PackageBookingFormValues,
        event?: { nativeEvent?: { submitter?: { dataset?: DOMStringMap } } }
    ) => {
        const isDraft = !!event?.nativeEvent?.submitter?.dataset?.draft;
        const dto = toCreatePackageBookingsReqDto(
            {
                ...values,
                status: isDraft ? BookingStatus.DRAFT : BookingStatus.HOLD,
            },
            selectedEvent
        );
        createPackageBooking(dto, {
            onSuccess: () => {
                onSuccess();
                showSuccess('CREATE_BOOKING_SUCCESS', {
                    name: selectedEvent.name,
                });
            },
            onError: () => {
                showError('CREATE_BOOKING_FAILURE', {
                    name: selectedEvent.name,
                });
            },
        });
    };

    return (
        <Stack sx={{ pl: 2, pr: 0.5, height: 1 }}>
            <PackageBookingSideformHead
                type={selectedEvent.type}
                name={selectedEvent.name}
                shared={!!selectedEvent?.shared}
            />

            <FormProvider {...methods}>
                <PackageBookingDateStartDuration
                    selectedEvent={selectedEvent}
                    selectedTime={selectedTime}
                />
                <form
                    id="blah"
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 2,
                        position: 'relative',
                        height: 'calc(100% - 144px)',
                        overflow: 'hidden',
                    }}
                    onSubmit={methods.handleSubmit(onSubmit)}
                >
                    <Stack
                        sx={{
                            flexGrow: 2,
                            minHeight: 0,
                            overflowY: 'auto',
                            gap: 3,
                            pr: 1,
                            pb: 2,
                        }}
                    >
                        <PackageProductOptions selectedEvent={selectedEvent} />
                        <PackageAvailableTimePicker
                            selectedEvent={selectedEvent}
                        />
                        <PackageCapacity selectedEvent={selectedEvent} />
                        <PackageBookingSourceSelection
                            productId={selectedEvent.id}
                        />
                        <PaxPicker
                            name="pax"
                            withDropdown
                            selectProps={{
                                disabled: true,
                                width: '100%',
                                label: t('PAX'),
                                placeholder: t('Select PAX'),
                            }}
                        />
                        <SelectCustomer
                            name="customer"
                            label={t('Customer Name or Email')}
                            placeholder={t('Type Customer Name or Email')}
                        />
                        <PackageBookingDiscount
                            maxAbsoluteDiscount={maxAbsoluteDiscount}
                        />
                        <PackageBookingNotesSelection />
                    </Stack>

                    <PackageBookingFooter
                        selectedEvent={selectedEvent}
                        pax={pax}
                        isSaving={isSaving}
                        onDiscard={discard}
                        onPriceResolved={setMaxAbsoluteDiscount}
                    />
                </form>
            </FormProvider>
            {isSaving && (
                <LoadingOverlay
                    title={t('Please wait...')}
                    subTitle={t('It’ll just take a moment.')}
                />
            )}
        </Stack>
    );
};

export default PackageBookingSideform;
